"use client";

import { useState } from "react";
import { createClient } from "@/lib/supabase/client";
import { trackCleanerApplication, trackFormSubmit } from "@/lib/analytics";
import { smsAdminNewCleanerApplication, getAdminSMSPrefs } from "@/lib/sms";
import { scoreApplicationWithClaude, saveApplicationScore } from "@/lib/cleanerScoring";

interface AppFormData {
  name: string;
  email: string;
  phone: string;
  experience: string;
  availability: string[];
  hoursPerWeek: string;
  hasTransportation: string;
  backgroundCheck: boolean;
  resumeFile: File | null;
  whyJoin: string;
}

const initialForm: AppFormData = {
  name: "",
  email: "",
  phone: "",
  experience: "",
  availability: [],
  hoursPerWeek: "",
  hasTransportation: "",
  backgroundCheck: false,
  resumeFile: null,
  whyJoin: "",
};

const availabilityOptions = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
];

export default function ApplicationForm() {
  const [form, setForm] = useState<AppFormData>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof AppFormData, string>>>({});
  const [submitError, setSubmitError] = useState("");

  const validate = () => {
    const e: Partial<Record<keyof AppFormData, string>> = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.phone.trim()) e.phone = "Phone is required";
    if (!form.experience) e.experience = "Please select your experience level";
    if (form.availability.length === 0) e.availability = "Select at least one available day";
    if (!form.backgroundCheck) e.backgroundCheck = "Background check consent is required";
    return e;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name as keyof AppFormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleAvailability = (day: string) => {
    setForm((prev) => ({
      ...prev,
      availability: prev.availability.includes(day)
        ? prev.availability.filter((d) => d !== day)
        : [...prev.availability, day],
    }));
    if (errors.availability) setErrors((prev) => ({ ...prev, availability: undefined }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setForm((prev) => ({ ...prev, resumeFile: file }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setSubmitting(true);
    setSubmitError("");

    try {
      const supabase = createClient();

      // Save application to Supabase
      const { data: insertedApp, error: dbError } = await supabase.from("cleaner_applications").insert({
        name: form.name,
        email: form.email,
        phone: form.phone,
        experience: form.experience,
        availability: form.availability,
        hours_per_week: form.hoursPerWeek,
        has_transportation: form.hasTransportation,
        background_check: form.backgroundCheck,
        why_join: form.whyJoin,
        app_status: "new",
      }).select('id').single();

      if (dbError) {
        console.error("DB error:", dbError.message);
        setSubmitError("Failed to submit. Please try again.");
        setSubmitting(false);
        trackCleanerApplication({ experience: form.experience, availability: form.availability, status: 'error' });
        trackFormSubmit('cleaner_application', 'error');
        return;
      }

      // Trigger AI scoring asynchronously (non-blocking)
      if (insertedApp?.id) {
        const appId = insertedApp.id;
        // Fetch admin prefs once, then fire SMS after scoring (with score) or immediately on failure (without score)
        getAdminSMSPrefs().then((adminPrefs) => {
          scoreApplicationWithClaude({
            name: form.name,
            experience: form.experience,
            availability: form.availability,
            hoursPerWeek: form.hoursPerWeek,
            hasTransportation: form.hasTransportation,
            whyJoin: form.whyJoin,
            backgroundCheck: form.backgroundCheck,
          }).then((score) => {
            if (score) saveApplicationScore(appId, score);
            if (adminPrefs) {
              smsAdminNewCleanerApplication({
                name: form.name,
                phone: form.phone,
                experienceLevel: score?.experience_level,
                availability: form.availability,
                adminUserId: adminPrefs.userId,
                adminPhone: adminPrefs.phone,
              });
            }
          }).catch((err) => {
            console.warn('[CleanerScoring] Async scoring error:', err);
            // Scoring failed — still send SMS without score
            if (adminPrefs) {
              smsAdminNewCleanerApplication({
                name: form.name,
                phone: form.phone,
                availability: form.availability,
                adminUserId: adminPrefs.userId,
                adminPhone: adminPrefs.phone,
              });
            }
          });
        }).catch((smsErr) => console.warn('[SMS] Admin cleaner application SMS failed:', smsErr));
      }

      // Send emails via server-side API route (avoids CORS preflight issues)
      try {
        await fetch("/api/send-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: "cleaner_application",
            data: {
              name: form.name,
              email: form.email,
              phone: form.phone,
              experience: form.experience,
              availability: form.availability,
              hoursPerWeek: form.hoursPerWeek,
              hasTransportation: form.hasTransportation,
              whyJoin: form.whyJoin,
            },
          }),
        });
      } catch (emailErr) {
        // Email failure is non-blocking — application is already saved
        console.warn("Email send failed:", emailErr);
      }

      setSubmitting(false);
      setSubmitted(true);
      // Track GA conversion event
      trackCleanerApplication({ experience: form.experience, availability: form.availability, status: 'success' });
      trackFormSubmit('cleaner_application', 'success');
    } catch (err) {
      console.error("Submit error:", err);
      setSubmitError("Something went wrong. Please try again.");
      setSubmitting(false);
      trackCleanerApplication({ experience: form.experience, availability: form.availability, status: 'error' });
      trackFormSubmit('cleaner_application', 'error');
    }
  };

  if (submitted) {
    return (
      <div className="bg-white border border-sand-medium rounded-3xl p-12 text-center shadow-luxury max-w-xl mx-auto">
        <div className="w-16 h-16 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="font-heading text-2xl text-navy mb-3">Application Submitted!</h3>
        <p className="text-navy/60 text-sm leading-relaxed mb-6">
          Thank you for applying to join the Premier team! We review all applications within 3–5 business days and will contact you by phone or email.
        </p>
        <p className="text-navy/40 text-xs">Questions? Call us at (850) 555-0192</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="bg-white border border-sand-medium rounded-3xl p-8 sm:p-10 shadow-luxury">
      <h3 className="font-heading text-2xl text-navy mb-2">Cleaner Application</h3>
      <p className="text-navy/50 text-sm mb-8">All fields marked with <span className="text-gold">*</span> are required.</p>

      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3">
          <svg className="w-5 h-5 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-red-600 text-sm">{submitError}</p>
        </div>
      )}

      {/* Personal Info */}
      <div className="mb-8">
        <h4 className="text-navy font-semibold text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-navy text-white text-xs flex items-center justify-center font-bold">1</span>
          Personal Information
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-navy text-sm font-semibold mb-2">Full Name <span className="text-gold">*</span></label>
            <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Maria Rodriguez" className={`input-luxury ${errors.name ? "border-red-400" : ""}`} />
            {errors.name && <p className="text-red-500 text-xs mt-1.5">{errors.name}</p>}
          </div>
          <div>
            <label className="block text-navy text-sm font-semibold mb-2">Email Address <span className="text-gold">*</span></label>
            <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="maria@example.com" className={`input-luxury ${errors.email ? "border-red-400" : ""}`} />
            {errors.email && <p className="text-red-500 text-xs mt-1.5">{errors.email}</p>}
          </div>
          <div>
            <label className="block text-navy text-sm font-semibold mb-2">Phone Number <span className="text-gold">*</span></label>
            <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="(850) 555-0100" className={`input-luxury ${errors.phone ? "border-red-400" : ""}`} />
            {errors.phone && <p className="text-red-500 text-xs mt-1.5">{errors.phone}</p>}
          </div>
          <div>
            <label className="block text-navy text-sm font-semibold mb-2">Reliable Transportation</label>
            <select name="hasTransportation" value={form.hasTransportation} onChange={handleChange} className="input-luxury">
              <option value="">Select...</option>
              <option value="yes">Yes, I have my own vehicle</option>
              <option value="no">No, I rely on public transport</option>
              <option value="sometimes">Sometimes</option>
            </select>
          </div>
        </div>
      </div>

      {/* Experience */}
      <div className="mb-8">
        <h4 className="text-navy font-semibold text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-navy text-white text-xs flex items-center justify-center font-bold">2</span>
          Experience
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-navy text-sm font-semibold mb-2">Cleaning Experience <span className="text-gold">*</span></label>
            <select name="experience" value={form.experience} onChange={handleChange} className={`input-luxury ${errors.experience ? "border-red-400" : ""}`}>
              <option value="">Select experience level</option>
              <option value="none">No formal experience</option>
              <option value="less-1">Less than 1 year</option>
              <option value="1-2">1–2 years</option>
              <option value="2-5">2–5 years</option>
              <option value="5+">5+ years</option>
            </select>
            {errors.experience && <p className="text-red-500 text-xs mt-1.5">{errors.experience}</p>}
          </div>
          <div>
            <label className="block text-navy text-sm font-semibold mb-2">Hours Available Per Week</label>
            <select name="hoursPerWeek" value={form.hoursPerWeek} onChange={handleChange} className="input-luxury">
              <option value="">Select range</option>
              <option value="10-20">10–20 hours</option>
              <option value="20-30">20–30 hours</option>
              <option value="30-40">30–40 hours</option>
              <option value="40+">40+ hours (Full-time)</option>
            </select>
          </div>
        </div>
        <div className="mt-5">
          <label className="block text-navy text-sm font-semibold mb-2">Why do you want to join Premier?</label>
          <textarea
            name="whyJoin"
            value={form.whyJoin}
            onChange={handleChange}
            rows={3}
            placeholder="Tell us a bit about yourself and why you'd be a great fit for our team..."
            className="input-luxury resize-none"
          />
        </div>
      </div>

      {/* Availability */}
      <div className="mb-8">
        <h4 className="text-navy font-semibold text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-navy text-white text-xs flex items-center justify-center font-bold">3</span>
          Availability <span className="text-gold ml-1">*</span>
        </h4>
        <p className="text-navy/50 text-sm mb-4">Select all days you&apos;re available to work.</p>
        <div className="flex flex-wrap gap-2">
          {availabilityOptions.map((day) => (
            <button
              key={day}
              type="button"
              onClick={() => handleAvailability(day)}
              className={`px-4 py-2.5 rounded-xl text-sm font-medium border-2 transition-all duration-200 ${
                form.availability.includes(day)
                  ? "border-navy bg-navy text-white" : "border-sand-medium bg-white text-navy/70 hover:border-navy/40"
              }`}
            >
              {day}
            </button>
          ))}
        </div>
        {errors.availability && <p className="text-red-500 text-xs mt-2">{errors.availability}</p>}
      </div>

      {/* Resume Upload */}
      <div className="mb-8">
        <h4 className="text-navy font-semibold text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-navy text-white text-xs flex items-center justify-center font-bold">4</span>
          Resume (Optional)
        </h4>
        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-sand-medium rounded-2xl cursor-pointer bg-sand hover:border-navy/30 transition-colors">
          <div className="flex flex-col items-center justify-center gap-2">
            <svg className="w-8 h-8 text-navy/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            {form.resumeFile ? (
              <span className="text-navy font-medium text-sm">{form.resumeFile.name}</span>
            ) : (
              <>
                <span className="text-navy/60 text-sm font-medium">Click to upload resume</span>
                <span className="text-navy/35 text-xs">PDF, DOC, DOCX up to 5MB</span>
              </>
            )}
          </div>
          <input type="file" className="hidden" accept=".pdf,.doc,.docx" onChange={handleFileChange} />
        </label>
      </div>

      {/* Background Check Consent */}
      <div className="mb-8">
        <label className={`flex items-start gap-4 p-5 rounded-2xl border-2 cursor-pointer transition-all duration-200 ${
          form.backgroundCheck ? "border-navy bg-navy/5" : errors.backgroundCheck ? "border-red-300 bg-red-50" : "border-sand-medium bg-sand"
        }`}>
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all ${
            form.backgroundCheck ? "bg-navy border-navy" : "border-navy/30 bg-white"
          }`}>
            {form.backgroundCheck && (
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <input
            type="checkbox"
            name="backgroundCheck"
            checked={form.backgroundCheck}
            onChange={handleChange}
            className="sr-only"
          />
          <div>
            <p className="text-navy font-semibold text-sm mb-1">
              Background Check Consent <span className="text-gold">*</span>
            </p>
            <p className="text-navy/55 text-xs leading-relaxed">
              I consent to Premier Property MGT conducting a background check as part of the hiring process. I understand this is required for all team members who access client properties.
            </p>
          </div>
        </label>
        {errors.backgroundCheck && <p className="text-red-500 text-xs mt-2">{errors.backgroundCheck}</p>}
      </div>

      <button
        type="submit"
        disabled={submitting}
        className={`btn-gold w-full py-4 rounded-xl font-semibold text-base flex items-center justify-center gap-3 ${
          submitting ? "opacity-75 cursor-not-allowed" : ""
        }`}
      >
        {submitting ? (
          <>
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Submitting Application...
          </>
        ) : (
          "Submit My Application"
        )}
      </button>
    </form>
  );
}